import { MonthYear } from './MonthYear/monthYear';

/**
 * Exposure horizon utility for consistent time window calculations across all views
 * 
 * This module provides a single source of truth for:
 * - Which months are included in exposure calculations
 * - Which data points are "in scope" vs "out of scope"
 * - Transparent display of the current horizon to users
 */

export interface ExposureHorizonWindow {
  /** Start date of the exposure window (typically "today" or start of current month) */
  startDate: MonthYear;
  /** End date of the exposure window (startDate + horizonMonths) */
  endDate: MonthYear;
  /** Number of months in the window */
  horizonMonths: number;
}

/**
 * Calculate the exposure window based on horizon months
 * @param horizonMonths Number of months forward from anchor (default: 12)
 * @param anchorDate Optional anchor date (defaults to current month)
 * @returns ExposureHorizonWindow with start, end, and horizon info
 */
export function getExposureWindow(
  horizonMonths: number = 12,
  anchorDate?: Date
): ExposureHorizonWindow {
  const anchor = anchorDate || new Date();
  
  // Start from the first day of the current month
  const startDate: MonthYear = {
    month: anchor.getMonth() + 1, // 1-indexed
    year: anchor.getFullYear(),
  };

  // Calculate end date: first day of current month + horizonMonths - 1
  const endDateObj = new Date(anchor.getFullYear(), anchor.getMonth() + horizonMonths - 1, 1);
  
  const endDate: MonthYear = {
    month: endDateObj.getMonth() + 1,
    year: endDateObj.getFullYear(),
  };

  return {
    startDate,
    endDate,
    horizonMonths,
  };
}

/**
 * Check if a given month/year is within the exposure horizon
 * @param date The date to check
 * @param window The exposure window
 * @returns true if the date is within the window
 */
export function isWithinHorizon(date: MonthYear, window: ExposureHorizonWindow): boolean {
  const dateValue = date.year * 12 + date.month;
  const startValue = window.startDate.year * 12 + window.startDate.month;
  const endValue = window.endDate.year * 12 + window.endDate.month;
  
  return dateValue >= startValue && dateValue <= endValue;
}

/**
 * Filter a monthly value array to only include months within the horizon
 * @param valueArray Array of {date, value} objects
 * @param window The exposure window
 * @returns Filtered array containing only in-scope months
 */
export function filterToHorizon<T extends { date: MonthYear; value: number }>(
  valueArray: T[],
  window: ExposureHorizonWindow
): T[] {
  return valueArray.filter(item => isWithinHorizon(item.date, window));
}

/**
 * Generate a formatted string describing the horizon for UI display
 * @param window The exposure window
 * @returns Human-readable string like "Jan 2026 – Dec 2026"
 */
export function formatHorizonDisplay(window: ExposureHorizonWindow): string {
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  const startMonth = monthNames[window.startDate.month - 1];
  const endMonth = monthNames[window.endDate.month - 1];
  
  return `${startMonth} ${window.startDate.year} – ${endMonth} ${window.endDate.year}`;
}

/**
 * Count how many items in a dataset are outside the horizon
 * Useful for displaying "X positions out of scope" messages
 * @param items Array of items with a date field
 * @param dateExtractor Function to extract MonthYear from an item
 * @param window The exposure window
 * @returns Count of out-of-scope items
 */
export function countOutOfScope<T>(
  items: T[],
  dateExtractor: (item: T) => MonthYear,
  window: ExposureHorizonWindow
): number {
  return items.filter(item => !isWithinHorizon(dateExtractor(item), window)).length;
}
